<template>
    <Head title="Create Incident Notification Policy" />

    <div class="max-w-3xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow rounded-lg">
            <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                        <div class="flex-1">
                            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Create Notification Policy</h2>
                            <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Configure who gets notified for specific incident conditions</p>
                        </div>
                        <InfoButton title="Creating Notification Policies" variant="default">
                            <p>
                                <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                The Create Notification Policy page allows you to establish automated email notification rules
                                that trigger when incidents matching specific criteria are reported. Each notification policy
                                defines matching conditions (incident type, near miss filter) and specifies recipient lists
                                (individual users or role-based groups) who should receive immediate notification emails.
                                Well-designed notification policies ensure rapid awareness of critical incidents, enable timely
                                response by routing notifications to appropriate personnel, support accountability through
                                automatic stakeholder notification, and reduce response delays by eliminating manual notification
                                steps. Notification policies work alongside SLA policies to create a comprehensive incident
                                response framework.
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                • <strong>Policy Name:</strong> Enter a clear, descriptive name that identifies the notification
                                  purpose (e.g., "Critical Safety - Notify Leadership", "Equipment Failures - Maintenance Team")<br>
                                • <strong>Description:</strong> Optionally explain what this policy covers, who receives
                                  notifications, and why this routing was established<br>
                                • <strong>Incident Type:</strong> Select a specific incident type to match, or leave as "All
                                  Types" to notify for any incident type<br>
                                • <strong>Near Miss Only:</strong> Check this box if this policy should only apply to near miss
                                  incidents (unchecked = applies to both incidents and near misses)<br>
                                • <strong>Recipient Type:</strong> Choose "Specific Users" to notify individual people, or
                                  "Roles" to notify all users assigned to specific roles<br>
                                • <strong>Select Users/Roles:</strong> Check boxes to select which users or roles should receive
                                  notifications - you must select at least one recipient<br>
                                • <strong>Selection Counter:</strong> Shows how many users/roles are currently selected<br>
                                • <strong>Active Status:</strong> Check to activate immediately, or leave unchecked to save as
                                  inactive for later activation<br>
                                • Click <strong>Create Policy</strong> to save, or <strong>Cancel</strong> to discard<br>
                                • Note: Leave matching criteria empty (All Types, Near Miss unchecked) to create a catch-all
                                  policy that notifies for every incident
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                • <strong>Prefer role-based recipients</strong> - Roles (like "Safety Manager", "Operations
                                  Director") are more maintainable than user lists because they don't break when staff changes<br>
                                • <strong>Start specific, expand later</strong> - Begin with targeted policies for critical
                                  incident types, then add broader policies as needed<br>
                                • <strong>Avoid notification fatigue</strong> - Too many notifications train people to ignore
                                  them - only notify those with actual responsibility<br>
                                • <strong>Match responsibilities</strong> - Route notifications based on who actually needs to
                                  respond, not organizational hierarchy<br>
                                • <strong>Consider time sensitivity</strong> - Critical incidents need immediate notification
                                  to on-call personnel, routine incidents can notify normal business contacts<br>
                                • <strong>Use descriptive names</strong> - Names should clearly indicate what triggers the
                                  notification and who receives it<br>
                                • <strong>Test with inactive status</strong> - Create policies as inactive, verify the
                                  configuration, then activate when ready<br>
                                • <strong>Document your routing logic</strong> - Use description field to explain why these
                                  specific people/roles were chosen<br>
                                • <strong>Combine with SLA policies</strong> - Notification policies alert people, SLA policies
                                  enforce response timeframes - use both together<br>
                                • <strong>Plan for 24/7 coverage</strong> - If your operation runs around the clock, ensure
                                  notification policies reach on-call personnel<br>
                                • <strong>Don't neglect near misses</strong> - Near miss notifications help prevent future
                                  serious incidents by enabling proactive response<br>
                                • <strong>Consider escalation needs</strong> - Create separate policies for initial notification
                                  (responders) and escalation notification (leadership)<br>
                                • <strong>Review recipient availability</strong> - Ensure selected users/roles have valid email
                                  addresses and actually check their email<br>
                                • <strong>Multiple policies can match</strong> - An incident can trigger multiple notification
                                  policies (e.g., one for type, one catch-all)<br>
                                • New policies apply immediately once activated - newly reported incidents will trigger
                                  notifications based on current active policies
                            </p>
                        </InfoButton>
                    </div>
                    <Link
                        :href="route('settings.incidents.notification-policies.index')"
                        class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                        Back
                    </Link>
                </div>
            </div>

            <form @submit.prevent="submit" class="px-6 py-6">
                <div class="space-y-6">
                    <!-- Name -->
                    <div>
                        <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Policy Name <span class="text-red-500 dark:text-red-400">*</span>
                        </label>
                        <input
                            type="text"
                            id="name"
                            v-model="form.name"
                            required
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            :class="{ 'border-red-300': form.errors.name }"
                            placeholder="e.g., Critical Safety Incidents"
                        />
                        <p v-if="form.errors.name" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ form.errors.name }}</p>
                    </div>

                    <!-- Description -->
                    <div>
                        <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                        <textarea
                            id="description"
                            v-model="form.description"
                            rows="3"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            placeholder="Optional description of when this policy applies..."
                        ></textarea>
                    </div>

                    <!-- Matching Criteria Section -->
                    <div class="pt-6 border-t border-gray-200 dark:border-gray-700">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Matching Criteria</h3>
                        <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">Leave criteria empty to match all incidents. More specific policies take precedence.</p>

                        <div class="space-y-4">
                            <!-- Incident Type -->
                            <div>
                                <label for="incident_type_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Incident Type</label>
                                <select
                                    id="incident_type_id"
                                    v-model="form.incident_type_id"
                                    class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                >
                                    <option value="">All Types</option>
                                    <option v-for="type in incidentTypes" :key="type.id" :value="type.id">
                                        {{ type.name }}
                                    </option>
                                </select>
                                <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">Leave empty to apply to all incident types</p>
                            </div>

                            <!-- Near Miss Only -->
                            <div class="flex items-start">
                                <div class="flex items-center h-5">
                                    <input
                                        id="near_miss_only"
                                        v-model="form.near_miss_only"
                                        type="checkbox"
                                        class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded"
                                    />
                                </div>
                                <div class="ml-3 text-sm">
                                    <label for="near_miss_only" class="font-medium text-gray-700 dark:text-gray-300">Near Miss Only</label>
                                    <p class="text-gray-500 dark:text-gray-400">Only apply this policy to near miss incidents</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Recipients Section -->
                    <div class="pt-6 border-t border-gray-200 dark:border-gray-700">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-4">Notification Recipients</h3>

                        <!-- Recipient Type -->
                        <div class="mb-4">
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Recipient Type <span class="text-red-500 dark:text-red-400">*</span>
                            </label>
                            <div class="space-y-2">
                                <label class="inline-flex items-center mr-6">
                                    <input
                                        type="radio"
                                        v-model="form.recipient_type"
                                        value="users"
                                        class="rounded-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span class="ml-2 text-gray-900 dark:text-white">Specific Users</span>
                                </label>
                                <label class="inline-flex items-center">
                                    <input
                                        type="radio"
                                        v-model="form.recipient_type"
                                        value="roles"
                                        class="rounded-full border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span class="ml-2 text-gray-900 dark:text-white">Roles</span>
                                </label>
                            </div>
                        </div>

                        <!-- Users Selection -->
                        <div v-if="form.recipient_type === 'users'">
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Select Users <span class="text-red-500 dark:text-red-400">*</span>
                            </label>
                            <div class="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-md p-3">
                                <label v-for="user in users" :key="user.id" class="flex items-center py-2 hover:bg-gray-50 dark:hover:bg-gray-600 px-2 rounded cursor-pointer">
                                    <input
                                        type="checkbox"
                                        :value="user.id"
                                        v-model="form.recipient_ids"
                                        class="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span class="ml-3 text-sm text-gray-900 dark:text-white">{{ user.name }}</span>
                                    <span class="ml-2 text-xs text-gray-500 dark:text-gray-400">({{ user.email }})</span>
                                </label>
                            </div>
                            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">{{ form.recipient_ids.length }} user(s) selected</p>
                        </div>

                        <!-- Roles Selection -->
                        <div v-if="form.recipient_type === 'roles'">
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Select Roles <span class="text-red-500 dark:text-red-400">*</span>
                            </label>
                            <div class="max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-md p-3">
                                <label v-for="role in roles" :key="role.id" class="flex items-center py-2 hover:bg-gray-50 dark:hover:bg-gray-600 px-2 rounded cursor-pointer">
                                    <input
                                        type="checkbox"
                                        :value="role.id"
                                        v-model="form.recipient_ids"
                                        class="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span class="ml-3 text-sm text-gray-900 dark:text-white">{{ role.name }}</span>
                                </label>
                            </div>
                            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">{{ form.recipient_ids.length }} role(s) selected</p>
                        </div>
                    </div>

                    <!-- Active Status -->
                    <div class="pt-6 border-t border-gray-200 dark:border-gray-700">
                        <div class="flex items-start">
                            <div class="flex items-center h-5">
                                <input
                                    id="is_active"
                                    v-model="form.is_active"
                                    type="checkbox"
                                    class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded"
                                />
                            </div>
                            <div class="ml-3 text-sm">
                                <label for="is_active" class="font-medium text-gray-700 dark:text-gray-300">Active</label>
                                <p class="text-gray-500 dark:text-gray-400">Enable this policy to start sending notifications</p>
                            </div>
                        </div>
                    </div>

                    <!-- Form Actions -->
                    <div class="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                        <Link
                            :href="route('settings.incidents.notification-policies.index')"
                            class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                            Cancel
                        </Link>
                        <button
                            type="submit"
                            :disabled="form.processing || form.recipient_ids.length === 0"
                            class="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                            {{ form.processing ? 'Creating...' : 'Create Policy' }}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { useSecureForm } from '@/Composables/useSecureForm';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    incidentTypes: {
        type: Array,
        required: true,
    },
    users: {
        type: Array,
        required: true,
    },
    roles: {
        type: Array,
        required: true,
    },
});

// Initialize form with useSecureForm for company context protection
const form = useSecureForm({
    name: '',
    description: '',
    incident_type_id: '',
    near_miss_only: false,
    recipient_type: 'users',
    recipient_ids: [],
    is_active: true,
});

const submit = () => {
    form.post(route('settings.incidents.notification-policies.store'), {
        preserveScroll: true,
    });
};
</script>
